/**
 * CJAS — banner templates with text layers.  Jira CA-40, CA-57.
 *
 * Staff make their own screen banners: Eid greetings, a fundraising drive, a
 * course announcement. The whole feature exists so nobody has to ring
 * Techspace to change a poster, which means it has to be impossible to
 * produce something broken.
 *
 * So this is a constrained template editor, not a canvas. Staff pick a
 * template and fill in text. They cannot move things, choose colours, or pick
 * a font — because a free canvas in the hands of six volunteers produces six
 * banners that look like six different mosques.
 */

const a11y = require('./a11y');
const tz = require('./tz');
const bannerArt = require('./banner-art');

/**
 * TV title-safe area. Broadcast convention is 10% inset, and cheap panels
 * genuinely do overscan — text at the edge of a 1080p signal gets cut off on
 * a wall-mounted screen and nobody notices until somebody photographs it.
 */
const SAFE_INSET_PERCENT = 5;
const TITLE_SAFE_PERCENT = 10;

/**
 * Three fixed palettes, each checked against WCAG AA by checkPalette().
 *
 * NOTE the accents. Brand gold #A0700F cannot be the accent on EITHER of the
 * two dark backgrounds:
 *   on brand green #086A3C  1.54:1  — invisible across a corridor
 *   on night      #0C1310   4.32:1  — under the 4.50 AA needs at accent size
 * The two brand colours simply cannot sit on each other as text, and the
 * brand gold is not light enough to be text on near-black either. So each
 * palette carries a text-safe gold, and the gold FILL is untouched wherever
 * it is used as a block. Same principle as --gold-text in tokens.css, and
 * the dark palette reuses that file's dark-mode value rather than inventing
 * a fourth gold.
 */
const PALETTES = {
  green:  { bg: '#086A3C', fg: '#FFFFFF', accent: '#EFD9A6', name_no: 'Grønn' },
  cream:  { bg: '#FFF8EA', fg: '#000000', accent: '#8F630C', name_no: 'Krem' },
  dark:   { bg: '#0C1310', fg: '#EAF1EC', accent: '#C9A227', name_no: 'Mørk' },
  // The designed templates' grounds (2026-10-22): night blue for Ramadan and the skyline, a deep red for a drive, warm
  // sand for daylight notices. Each passes the same check (body ≥ 4.5, the accent ≥ 4.5 as normal-size text).
  navy:     { bg: '#0F2A44', fg: '#FFFFFF', accent: '#EBCB7E', name_no: 'Nattblå' },
  burgundy: { bg: '#5C1A2B', fg: '#FFFFFF', accent: '#F0D49A', name_no: 'Vinrød' },
  sand:     { bg: '#F4E8D0', fg: '#1D1A14', accent: '#7A520C', name_no: 'Sand' },
  // CJAS's own posts: green lettering on a light ground (fg 6.26:1, accent 4.96:1).
  ivory:    { bg: '#FBF7EE', fg: '#086A3C', accent: '#8F630C', name_no: 'Elfenben' },
};

/**
 * The Arabic line (2026-10-22, from CJAS's own posts, where the calligraphy is the centrepiece). Staff CHOOSE one of
 * these — nobody types Arabic, and nothing here is machine-translated: the layer stores the key, render() draws the
 * words. The list is the mosque's to review, as the duas are.
 */
const ARABIC = {
  jumuah:    { ar: 'جمعة مباركة',              name_no: "Jumu'ah Mubarak" },
  eid:       { ar: 'عيد مبارك',                name_no: 'Eid Mubarak' },
  eidFitr:   { ar: 'عيد الفطر مبارك',          name_no: 'Eid al-Fitr Mubarak' },
  eidAdha:   { ar: 'عيد الأضحى مبارك',         name_no: 'Eid al-Adha Mubarak' },
  ramadan:   { ar: 'رمضان كريم',               name_no: 'Ramadan Kareem' },
  rabi:      { ar: 'ربيع الأول مبارك',         name_no: 'Rabi-ul-Awwal Mubarak' },
  milad:     { ar: 'ميلاد النبي ﷺ',            name_no: 'Milad-un-Nabi ﷺ' },
  hijri:     { ar: 'عام هجري سعيد',            name_no: 'Godt nytt hijri-år' },
  salam:     { ar: 'السلام عليكم',             name_no: 'Assalamu alaikum' },
  bismillah: { ar: 'بسم الله الرحمن الرحيم',   name_no: 'Bismillah' },
  jazak:     { ar: 'جزاكم الله خيرا',          name_no: 'Jazakum Allahu khayran' },
};
/** The Arabic layer, the same in every template that has one: a choice from ARABIC, never typed. */
const arabicLayer = { id: 'arabic', role: 'calligraphy', maxChars: 40, required: false, sizePct: 6.5, weight: 700, options: Object.keys(ARABIC) };
/** A layer whose words are a fixed choice is not translated (its stored value is a key). */
const translatable = (layerId) => layerId !== 'arabic';

/**
 * Three plain templates, and six designed ones (2026-10-22).
 *
 * Each declares its layers with a character budget worked back from the font
 * size and the safe area — that is what makes overflow catchable before the
 * banner reaches a wall rather than after.
 *
 * A designed template adds a decoration (banner-art.js) drawn behind the text, the palette it looks best in
 * (`palette`; staff can still pick another), a headline to start from (`starter`) and a hint per field
 * (`placeholder_no`). Still no canvas: the ornaments, their places and the text area are fixed.
 */
const TEMPLATES = {
  announcement: {
    id: 'announcement',
    name_no: 'Kunngjøring',
    ratio: '16:9',
    layers: [
      { id: 'eyebrow',  role: 'label',    maxChars: 28,  required: false, sizePct: 3.0, weight: 500 },
      { id: 'headline', role: 'headline', maxChars: 42,  required: true,  sizePct: 8.5, weight: 700, lines: 2 },
      { id: 'detail',   role: 'body',     maxChars: 90,  required: false, sizePct: 4.0, weight: 400, lines: 2 },
      { id: 'when',     role: 'meta',     maxChars: 40,  required: false, sizePct: 3.6, weight: 600 },
    ],
  },
  event: {
    id: 'event',
    name_no: 'Arrangement',
    ratio: '16:9',
    layers: [
      { id: 'headline', role: 'headline', maxChars: 36,  required: true,  sizePct: 9.0, weight: 700, lines: 2 },
      { id: 'when',     role: 'meta',     maxChars: 44,  required: true,  sizePct: 4.4, weight: 600 },
      { id: 'where',    role: 'meta',     maxChars: 44,  required: false, sizePct: 3.6, weight: 400 },
      { id: 'detail',   role: 'body',     maxChars: 110, required: false, sizePct: 3.4, weight: 400, lines: 3 },
    ],
  },
  greeting: {
    id: 'greeting',
    name_no: 'Hilsen',
    ratio: '16:9',
    layers: [
      { id: 'headline', role: 'display',  maxChars: 24,  required: true,  sizePct: 13.0, weight: 700, lines: 1 },
      { id: 'detail',   role: 'body',     maxChars: 70,  required: false, sizePct: 4.0,  weight: 400, lines: 2 },
    ],
  },
  eid: {
    id: 'eid', name_no: 'Eid-hilsen', ratio: '16:9', decor: 'crescent', palette: 'green', arabic: 'eid', starter: 'Eid Mubarak',
    layers: [
      arabicLayer,
      { id: 'eyebrow',  role: 'label',   maxChars: 28, required: false, sizePct: 3.0, weight: 500, placeholder_no: 'F.eks. Fra styret og imamen' },
      { id: 'headline', role: 'display', maxChars: 20, required: true,  sizePct: 11.0, weight: 700, lines: 1 },
      { id: 'detail',   role: 'body',    maxChars: 60, required: false, sizePct: 3.6, weight: 400, lines: 2, placeholder_no: 'F.eks. Eid-bønn i hovedsalen — tid kommer' },
    ],
  },
  ramadan: {
    id: 'ramadan', name_no: 'Ramadan', ratio: '16:9', decor: 'lanterns', palette: 'navy', arabic: 'ramadan', starter: 'Ramadan Kareem',
    layers: [
      arabicLayer,
      { id: 'eyebrow',  role: 'label',   maxChars: 28, required: false, sizePct: 3.0, weight: 500, placeholder_no: 'F.eks. Velkommen til' },
      { id: 'headline', role: 'display', maxChars: 22, required: true,  sizePct: 10.0, weight: 700, lines: 1 },
      { id: 'detail',   role: 'body',    maxChars: 70, required: false, sizePct: 3.6, weight: 400, lines: 2, placeholder_no: 'F.eks. Tarawih hver kveld etter isha' },
    ],
  },
  jumuah: {
    id: 'jumuah', name_no: "Jumu'ah", ratio: '16:9', decor: 'arch', palette: 'green', arabic: 'jumuah', starter: "Jumu'ah Mubarak",
    layers: [
      arabicLayer,
      { id: 'headline', role: 'headline', maxChars: 24, required: true,  sizePct: 7.5, weight: 700, lines: 1 },
      { id: 'when',     role: 'meta',     maxChars: 40, required: false, sizePct: 4.0, weight: 600, placeholder_no: 'F.eks. Khutbah kl. 13:15 · bønn kl. 13:45' },
      { id: 'detail',   role: 'body',     maxChars: 70, required: false, sizePct: 3.2, weight: 400, lines: 2, placeholder_no: 'F.eks. Husk å komme i god tid' },
    ],
  },
  skyline: {
    id: 'skyline', name_no: 'Arrangement med moské', ratio: '16:9', decor: 'skyline', palette: 'navy',
    layers: [
      { id: 'headline', role: 'headline', maxChars: 34, required: true,  sizePct: 7.5, weight: 700, lines: 2, placeholder_no: 'F.eks. Mahfil-e-Milad' },
      { id: 'when',     role: 'meta',     maxChars: 44, required: true,  sizePct: 4.0, weight: 600, placeholder_no: 'F.eks. Lørdag 24. oktober kl. 18:00' },
      { id: 'where',    role: 'meta',     maxChars: 44, required: false, sizePct: 3.2, weight: 400, placeholder_no: 'F.eks. Hovedsalen' },
    ],
  },
  pattern: {
    id: 'pattern', name_no: 'Kunngjøring med mønster', ratio: '16:9', decor: 'pattern', palette: 'sand',
    layers: [
      { id: 'eyebrow',  role: 'label',    maxChars: 28, required: false, sizePct: 3.0, weight: 600, placeholder_no: 'F.eks. Viktig beskjed' },
      { id: 'headline', role: 'headline', maxChars: 40, required: true,  sizePct: 7.0, weight: 700, lines: 2, placeholder_no: 'F.eks. Parkeringen er stengt lørdag' },
      { id: 'detail',   role: 'body',     maxChars: 90, required: false, sizePct: 3.4, weight: 400, lines: 2, placeholder_no: 'F.eks. Bruk gateparkering eller kom med buss' },
    ],
  },
  fundraiser: {
    id: 'fundraiser', name_no: 'Innsamling', ratio: '16:9', decor: 'frame', palette: 'burgundy',
    layers: [
      { id: 'eyebrow',  role: 'label',    maxChars: 28, required: false, sizePct: 3.0, weight: 600, placeholder_no: 'F.eks. Byggefond' },
      { id: 'headline', role: 'headline', maxChars: 36, required: true,  sizePct: 7.5, weight: 700, lines: 2, placeholder_no: 'F.eks. Bygg fremtidens moské med oss' },
      { id: 'detail',   role: 'body',     maxChars: 90, required: false, sizePct: 3.4, weight: 400, lines: 2, placeholder_no: 'F.eks. Hver gave teller' },
      { id: 'when',     role: 'meta',     maxChars: 40, required: false, sizePct: 4.0, weight: 700, placeholder_no: 'F.eks. Gi i appen eller med Vipps' },
    ],
  },
  // In the style of CJAS's own posts (owner's Drive folder, 2026-10-22).
  jumuah_light: {
    id: 'jumuah_light', name_no: "Jumu'ah (lys)", ratio: '16:9', decor: 'daylight', palette: 'ivory', arabic: 'jumuah', starter: "Jumu'ah Mubarak",
    layers: [
      arabicLayer,
      { id: 'headline', role: 'headline', maxChars: 24, required: true,  sizePct: 7.0, weight: 700, lines: 1 },
      { id: 'when',     role: 'meta',     maxChars: 40, required: false, sizePct: 4.2, weight: 700, placeholder_no: 'F.eks. Salah kl. 14:00' },
      { id: 'detail',   role: 'body',     maxChars: 70, required: false, sizePct: 3.0, weight: 400, lines: 2, placeholder_no: 'F.eks. Khutbah ved imamen' },
    ],
  },
  eid_line: {
    id: 'eid_line', name_no: 'Eid (strektegning)', ratio: '16:9', decor: 'lineart', palette: 'ivory', arabic: 'eid', starter: 'Eid Mubarak',
    layers: [
      arabicLayer,
      { id: 'headline', role: 'headline', maxChars: 24, required: true,  sizePct: 7.5, weight: 700, lines: 1, placeholder_no: 'F.eks. Eid-al-Adha' },
      { id: 'when',     role: 'meta',     maxChars: 40, required: false, sizePct: 3.8, weight: 600, placeholder_no: 'F.eks. Onsdag 27. mai · jamaat kl. 10:00' },
      { id: 'detail',   role: 'body',     maxChars: 70, required: false, sizePct: 3.0, weight: 400, lines: 2, placeholder_no: 'F.eks. Khutbah ved imamen' },
    ],
  },
  occasion: {
    id: 'occasion', name_no: 'Islamsk merkedag', ratio: '16:9', decor: 'spotlight', palette: 'green', arabic: 'rabi',
    layers: [
      arabicLayer,
      { id: 'headline', role: 'headline', maxChars: 32, required: true,  sizePct: 6.5, weight: 700, lines: 1, placeholder_no: 'F.eks. God Rabi-ul-Awwal' },
      { id: 'detail',   role: 'body',     maxChars: 110, required: false, sizePct: 3.0, weight: 400, lines: 3, placeholder_no: 'F.eks. En hilsen til menigheten' },
    ],
  },
  notice_light: {
    id: 'notice_light', name_no: 'Kunngjøring (lys)', ratio: '16:9', decor: 'swoosh', palette: 'ivory',
    layers: [
      { id: 'eyebrow',  role: 'label',    maxChars: 28, required: false, sizePct: 3.0, weight: 600, placeholder_no: 'F.eks. Beskjed fra styret' },
      { id: 'headline', role: 'headline', maxChars: 40, required: true,  sizePct: 7.0, weight: 700, lines: 2, placeholder_no: 'F.eks. Ny skohylle ved inngangen' },
      { id: 'detail',   role: 'body',     maxChars: 100, required: false, sizePct: 3.2, weight: 400, lines: 2, placeholder_no: 'F.eks. Sett skoene i hyllen, ikke i gangen' },
    ],
  },
  /**
   * «Bilde med tekst» (banner studio, 2026-10-31): the mosque's own photo behind the text, under a scrim in the
   * palette's colour (banner-art `scrim`) so the words read over any picture. The photo is stored as a picture
   * banner's is (banner_image); the text layers are translated as any text banner's. No ornament choice here: the scrim
   * is the design, and it is what keeps the contrast.
   */
  photo: {
    id: 'photo', name_no: 'Bilde med tekst', ratio: '16:9', decor: 'scrim', palette: 'dark', photo: true,
    layers: [
      { id: 'eyebrow',  role: 'label',    maxChars: 28, required: false, sizePct: 3.0, weight: 600, placeholder_no: 'F.eks. Velkommen til' },
      { id: 'headline', role: 'headline', maxChars: 34, required: true,  sizePct: 8.0, weight: 700, lines: 2, placeholder_no: 'F.eks. Mahfil-e-Milad' },
      { id: 'when',     role: 'meta',     maxChars: 44, required: false, sizePct: 4.0, weight: 600, placeholder_no: 'F.eks. Lørdag 24. oktober kl. 18:00' },
      { id: 'detail',   role: 'body',     maxChars: 80, required: false, sizePct: 3.2, weight: 400, lines: 2, placeholder_no: 'F.eks. Hovedsalen · alle er velkomne' },
    ],
  },
};

/** The templates in the order the editor shows them: the designed ones first, then the plain three. */
const TEMPLATE_ORDER = ['photo', 'jumuah_light', 'eid_line', 'occasion', 'notice_light', 'eid', 'ramadan', 'jumuah', 'skyline', 'pattern', 'fundraiser',
  'greeting', 'announcement', 'event'];

/**
 * The studio's «Pynt» (2026-10-31): any text design can carry any of banner-art's ORNAMENTS, or none, instead of the
 * one its template was drawn with. The choice is stored on the banner (`decor`: null = the template's own, 'none', or
 * an ornament id) and travels inside render()'s result, so the screens, the app, the preview, «Del» and «Skriv ut» all
 * draw the same thing. A photo banner always draws its scrim — nothing else keeps its text readable.
 */
const ORNAMENTS = bannerArt.ORNAMENTS;
const ORNAMENT_IDS = ORNAMENTS.map((o) => o.id);
/** A stored or typed ornament choice: null (the template's own), 'none', or one of ORNAMENTS. */
const validDecor = (v) => v === null || v === undefined || v === '' || v === 'none' || ORNAMENT_IDS.includes(v);
/** Which ornament a banner draws, from its template and its choice. */
function decorOf(tpl, choice) {
  if (!tpl) return null;
  if (tpl.photo) return tpl.decor;
  if (choice === undefined || choice === null || choice === '') return tpl.decor || null;
  if (choice === 'none') return null;
  return ORNAMENT_IDS.includes(choice) ? choice : tpl.decor || null;
}

// SI-06 (audit 2026-10-19): an id from a request body is looked up as an OWN key only — `PALETTES['constructor']` was
// Object itself, truthy, and passed the check.
const own = (table, id) => (typeof id === 'string' && Object.hasOwn(table, id) ? table[id] : null);
function template(id) { return own(TEMPLATES, id); }
function palette(id) { return own(PALETTES, id); }

/**
 * A rough line count. Not typesetting — the point is to catch "this will
 * clearly not fit" before the banner is on a wall, not to be exact.
 */
function estimateLines(text, maxChars) {
  const t = String(text || '').trim();
  if (!t) return 0;
  return t.split('\n').reduce((n, line) => n + Math.max(1, Math.ceil(line.length / maxChars)), 0);
}

/**
 * Validate the filled-in layers.
 *
 * Over-long text is a WARNING with a shrink suggestion rather than a hard
 * error, because refusing to save a banner that is two characters too long is
 * how staff stop using the feature. Only a missing required layer blocks.
 */
function validate(templateId, values = {}, lang = 'no') {
  const tpl = template(templateId);
  if (!tpl) return { ok: false, errors: { _form: 'Ukjent mal.' }, warnings: {} };

  const errors = {}, warnings = {}, fit = {};

  for (const layer of tpl.layers) {
    const raw = values[layer.id];
    const text = typeof raw === 'string' ? raw.trim() : '';

    if (layer.required && !text) {
      errors[layer.id] = 'Dette feltet må fylles ut.';
      continue;
    }
    if (!text) { fit[layer.id] = { lines: 0, fits: true }; continue; }
    if (layer.options) {
      if (!layer.options.includes(text)) errors[layer.id] = 'Velg en av tekstene i listen.';
      fit[layer.id] = { lines: 1, fits: true };
      continue;
    }

    const maxLines = layer.lines || 1;
    const lines = estimateLines(text, layer.maxChars);
    const fits = lines <= maxLines && text.length <= layer.maxChars * maxLines;

    fit[layer.id] = {
      lines, maxLines, chars: text.length,
      budget: layer.maxChars * maxLines, fits,
      // What the editor shows under the field, live.
      remaining: layer.maxChars * maxLines - text.length,
    };

    if (!fits) {
      warnings[layer.id] = `Teksten er lengre enn malen har plass til. Den blir mindre på skjermen — vurder å korte den ned til ca. ${layer.maxChars * maxLines} tegn.`;
    }
  }

  // Urdu in Nastaliq needs far more vertical room than Latin at the same
  // size, so the budget is tighter. Not a warning staff can fix by rewording
  // in Norwegian — it is about the Urdu line specifically.
  if (lang === 'ur') {
    for (const layer of tpl.layers) {
      const text = String(values[layer.id] || '').trim();
      if (layer.options) continue;
      if (text && text.length > layer.maxChars * 0.7 * (layer.lines || 1)) {
        warnings[layer.id] = 'Urdu-tekst tar mer plass i høyden. Kort den gjerne ned.';
      }
    }
  }

  return { ok: Object.keys(errors).length === 0, errors, warnings, fit };
}

/**
 * Shrink to fit rather than clipping.
 *
 * Clipped text on a screen in a corridor reads as a broken system. Smaller
 * text reads as a long announcement. There is a floor, though — below it the
 * banner is unreadable from across a room and the honest answer is that the
 * text is too long.
 */
// 3% of screen height is ~32px on a 1080p panel. Below that, text on a wall
// screen is not readable from across a corridor, which is the only place these
// are ever seen — so this is a real floor, not a rendering minimum.
const MIN_SIZE_PCT = 3.0;
function fitSize(layer, text) {
  const maxLines = layer.lines || 1;
  const budget = layer.maxChars * maxLines;
  const len = String(text || '').length;
  if (len <= budget) return { sizePct: layer.sizePct, shrunk: false, ratio: 1 };
  const ratio = Math.sqrt(budget / len);
  const sizePct = Math.max(MIN_SIZE_PCT, Math.round(layer.sizePct * ratio * 10) / 10);
  return {
    sizePct, shrunk: true, ratio: Math.round(ratio * 100) / 100,
    tooSmall: sizePct <= MIN_SIZE_PCT,
  };
}

/**
 * Render model. Sizes are percentages of the screen height, not pixels, so
 * one banner is correct on a 32" panel and a 75" one — the same reason
 * tv.html uses container-query units.
 */
function render(templateId, values = {}, opts = {}) {
  const tpl = template(templateId);
  if (!tpl) return null;
  const palette = own(PALETTES, opts.palette) || PALETTES.green;
  const lang = opts.lang || 'no';

  const pal = own(PALETTES, opts.palette) ? opts.palette : 'green';
  const layers = tpl.layers
    .filter(l => String(values[l.id] || '').trim() && (!l.options || l.options.includes(String(values[l.id]).trim())))
    .map(l => {
      // A chosen line (the Arabic) is drawn from its key; it is never typed, so it never shrinks.
      const text = l.options ? ARABIC[String(values[l.id]).trim()].ar : String(values[l.id]).trim();
      const size = l.options ? { sizePct: l.sizePct, shrunk: false } : fitSize(l, text);
      return {
        id: l.id, role: l.role, text,
        sizePct: size.sizePct, shrunk: size.shrunk, tooSmall: Boolean(size.tooSmall),
        weight: l.weight,
        colour: l.role === 'label' || l.role === 'meta' ? palette.accent : palette.fg,
        // Nastaliq needs the extra leading; see i18n.layout(). Arabic calligraphy is Arabic whatever the banner's language.
        lineHeight: l.options ? 1.4 : lang === 'ur' ? 2.1 : 1.25,
        dir: l.options || lang === 'ur' ? 'rtl' : 'ltr',
        ...(l.options ? { lang: 'ar', fixed: true } : {}),
      };
    });

  // The decoration in THIS palette (2026-10-22): its pieces for the app, and the same as HTML for the TV and the panel.
  // The studio's choice (2026-10-31) replaces the template's own, or removes it; a photo keeps its scrim.
  const decorId = decorOf(tpl, opts.decor);
  const decor = decorId ? bannerArt.art(decorId, palette) : null;
  return {
    template: tpl.id, ratio: tpl.ratio, palette, paletteId: pal, lang,
    // A photo banner: the renderer draws the banner's picture behind the pieces, covering the whole banner.
    photo: Boolean(tpl.photo),
    layers,
    decor: decor ? { id: decor.id, pieces: decor.pieces, inset: decor.inset, html: bannerArt.artHtml(decor) } : null,
    safeInsetPercent: SAFE_INSET_PERCENT,
    titleSafePercent: TITLE_SAFE_PERCENT,
    anyShrunk: layers.some(l => l.shrunk),
    anyUnreadable: layers.some(l => l.tooSmall),
  };
}

/**
 * Contrast is checked, not trusted.
 *
 * The palettes are fixed and all three pass, so this is a guard against
 * somebody adding a fourth one later. A banner nobody can read from the far
 * end of a corridor is a banner that did not happen.
 */
function checkPalette(paletteId) {
  const p = own(PALETTES, paletteId);
  if (!p) return { ok: false, reason: 'unknown_palette' };
  // The headline is genuinely large (40px). The accent label is not — at 20px
  // it is normal-size text under WCAG and needs the full 4.50, which is the
  // whole reason the palettes carry a text-safe gold.
  const body = a11y.meetsAA(p.fg, p.bg, 40);
  const accent = a11y.meetsAA(p.accent, p.bg, 20);
  return {
    ok: body.pass && accent.pass,
    body: body.ratio, accent: accent.ratio,
    required: body.required,
    accentRequired: accent.required,
  };
}

/**
 * Banners expire. A banner with no end date is a Ramadan greeting still on
 * the wall in July, which is the single most common failure of every mosque
 * screen system.
 */
const DEFAULT_RUN_DAYS = 14;
function schedule(startsAt, endsAt, now = new Date(), zone = null) {
  // A wall-clock time is read on the mosque's wall (`zone`), as isLiveNow() reads it (banner port review, 2026-10-14).
  const start = startsAt ? new Date(instantOf(startsAt, zone)) : new Date(now);
  const end = endsAt ? new Date(instantOf(endsAt, zone))
    : new Date(start.getTime() + DEFAULT_RUN_DAYS * 86400000);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return { ok: false, reason: 'invalid_date' };
  if (end <= start) return { ok: false, reason: 'ends_before_starts' };
  return {
    ok: true, startsAt: start.toISOString(), endsAt: end.toISOString(),
    autoExpiry: !endsAt,
    days: Math.round((end - start) / 86400000),
    note_no: !endsAt ? `Banneret fjernes automatisk etter ${DEFAULT_RUN_DAYS} dager.` : null,
  };
}

/* ------------------------------------------------------------------------------------------------------------------
 * "Live now" — banner access patch (2026-10-04, docs/platform-implementation/BANNER_ACCESS_PATCH.md).
 *
 * One rule for every public banner answer: the screens' feed (active()) and the picture route (isLiveNow()). A banner
 * is live when its stored status is `published` or `scheduled` (approved; `scheduled` only means it was approved
 * before its start — see the lifecycle below; draft, cancelled and archived are never shown), it has started (no
 * start = from now) and it has not ended.
 *
 * NO END DATE: a published banner without `ends_at` is live until somebody takes it down (draft or archived). That is
 * what the screens have always done, and this patch keeps it: no crash, no invented expiry, no change to any row.
 * A maximum age for such banners is a later decision (see the doc), not something this predicate guesses at.
 *
 * Times: the panel stores what a datetime-local field sends, a wall-clock time with no zone ("2026-10-25T12:00").
 * That is read on the MOSQUE's wall (`zone`; the platform only serves mosques in its own zone, so this equals the
 * process zone the screens' feed always used). A value that carries its own offset or Z is an instant as it stands.
 * A value that cannot be read is not live (fail closed, as before).
 * ---------------------------------------------------------------------------------------------------------------- */
const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3})\d*)?)?$/;

/** The zone's offset from UTC at an instant, in ms (minute resolution, which every real zone fits). */
function offsetAt(t, zone) {
  const p = tz.partsIn(new Date(t), zone);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute) - Math.floor(t / 60000) * 60000;
}

/**
 * A stored start or end as epoch ms: null when there is none, NaN when it cannot be read. A wall-clock value is
 * placed on `zone`'s wall; in the hour a clock skips or repeats it resolves to one fixed instant.
 */
function instantOf(v, zone) {
  if (v === null || v === undefined || v === '') return null;
  if (v instanceof Date) return v.getTime();
  const s = String(v);
  const m = WALL_CLOCK.exec(s);
  if (!m) return Date.parse(s);
  const asUtc = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0), +((m[7] || '0').padEnd(3, '0')));
  if (Number.isNaN(asUtc)) return NaN;
  // Out-of-range parts (month 13, day 45, 25:99) must not roll over into a real date: not readable = not live.
  const back = new Date(asUtc);
  if (back.getUTCFullYear() !== +m[1] || back.getUTCMonth() !== +m[2] - 1 || back.getUTCDate() !== +m[3]
      || back.getUTCHours() !== +m[4] || back.getUTCMinutes() !== +m[5] || back.getUTCSeconds() !== +(m[6] || 0)) return NaN;
  const guess = asUtc - offsetAt(asUtc, zone);
  return asUtc - offsetAt(guess, zone);
}

/** Started (or no start) and not ended (or NO END: live until taken down). */
function inWindow(b, now = new Date(), zone = null) {
  const t = now.getTime();
  const start = instantOf(b && b.startsAt, zone);
  const end = instantOf(b && b.endsAt, zone);
  if (start !== null && !(start <= t)) return false;   // not started, or unreadable
  if (end !== null && !(end > t)) return false;        // ended, or unreadable
  return true;
}

/**
 * Whether a stored status may be shown at all: `published`, or `scheduled` WITH an approval (banner port to main,
 * 2026-10-14). Main's panel never stored `scheduled`; only the lifecycle's approval does, and it always sets
 * `approved_by`. A `scheduled` row without one (from any other path) stays hidden, as the access patch promised.
 */
function shownStatus(b) {
  return Boolean(b) && (b.status === 'published' || (b.status === 'scheduled' && Boolean(b.approvedBy)));
}

/** Whether a stored banner `{ status, approvedBy, startsAt, endsAt }` may be shown publicly right now. */
function isLiveNow(b, now = new Date(), zone = null) {
  return shownStatus(b) && inWindow(b, now, zone);
}

/**
 * How long a PUBLIC cache may keep a live banner's picture, in whole seconds: at most `max` (five minutes), and never
 * past the banner's own end — a banner ending in 90 seconds is cached for 89, not 300, so no copy outlives it.
 * The whole seconds left, rounded DOWN, less ONE: a cache counts its lifetime from when the answer reaches it, a
 * little after `now` (the request's start), and that second absorbs the time to build and deliver the answer.
 * Under two seconds left is 0 (revalidate every time). No end date = `max` (live until taken down; the five
 * minutes bound a take-down). Only meaningful for a banner
 * isLiveNow() accepted — anything else is never cached at all (the route answers 404 no-store) — so an end that is
 * not in the future, or cannot be read, is 0 here rather than a guess (2026-10-04, banner cache P3).
 */
function publicCacheSeconds(b, now = new Date(), zone = null, max = 300) {
  const end = instantOf(b && b.endsAt, zone);
  if (end === null) return max;
  const left = Math.floor((end - now.getTime()) / 1000) - 1;   // one second for building and delivering the answer
  if (!(left > 0)) return 0;
  return Math.min(max, left);
}

/**
 * Which banners are live now, newest first, capped so the rotation stays short. Same window rule as isLiveNow(). Ties
 * (the same start) are broken by id, so the order is the same on every screen and every phone, every time (2026-10-03).
 */
function active(banners, now = new Date(), max = 4, zone = null) {
  return (banners || [])
    .filter(b => b.published)
    .filter(b => inWindow(b, now, zone))
    .sort((a, b) => ((instantOf(b.startsAt, zone) || 0) - (instantOf(a.startsAt, zone) || 0)) || (String(a.id) < String(b.id) ? -1 : String(a.id) > String(b.id) ? 1 : 0))
    .slice(0, max);
}

/* ------------------------------------------------------------------------------------------------------------------
 * The lifecycle (banner hardening, 2026-10-03; docs/platform-implementation/BANNER_LIFECYCLE.md).
 *
 * A banner is made and edited as a DRAFT by anyone who runs the screens (screens.manage). It reaches a wall or a phone
 * when it is published: by the person who wrote it (made or changed it, with screens.manage), or by anybody with
 * banners.approve. Owner, 2026-10-23: "who write can publish" — this replaces the four-eyes rule of 2026-10-03
 * (BA-D01/BA-D03). Publishing gives it an end (the 14-day default when none was set) and makes it `published`, or
 * `scheduled` when it starts later. Taking it down is easy and needs no second person: `cancelled` (stopped; can go
 * back to draft and be approved again), or `archived` (final). Changing an approved banner means taking it back to a
 * draft first, which clears the approval — what was approved is what is shown.
 *
 * `expired` is not a stored status: it is a published or scheduled banner whose end has passed, and it is never shown.
 * ---------------------------------------------------------------------------------------------------------------- */

const STATUSES = ['draft', 'scheduled', 'published', 'cancelled', 'archived'];
/** Stored statuses that can be on a wall or a phone, inside their window. */
const SHOWN_STATUSES = ['published', 'scheduled'];
/** The longest window a banner chosen for the member app may run. Walls are not limited (they never were). */
const MAX_APP_DAYS = 31;
/** At most this many banners in the member app, and in what order: `active()`'s. */
const MEMBER_LIMIT = 3;

const ms = (v) => (v ? Date.parse(v) : NaN);
const validDate = (v) => v == null || v === '' || !Number.isNaN(Date.parse(v));

/** What a banner is NOW: draft · scheduled (approved, not started) · live · expired · cancelled · archived. */
function stateOf(b, now = new Date(), zone = null) {
  const status = b && b.status;
  if (status === 'archived' || status === 'cancelled' || status === 'draft') return status;
  if (!shownStatus(b)) return 'draft';                               // anything else is never shown
  // The same reading of times as isLiveNow() (the mosque's wall clock; unreadable = not live), so the panel's word
  // and what the screens and the app show never disagree.
  if (inWindow(b, now, zone)) return 'live';
  const t = now.getTime(), end = instantOf(b.endsAt, zone);
  if (end === null || end > t) return 'scheduled';
  return 'expired';
}
const isLive = (b, now = new Date(), zone = null) => stateOf(b, now, zone) === 'live';

/**
 * Whether this person has had a hand in the banner's content: made it, or changed it at ANY point (review P1-1,
 * 2026-10-03 — "last editor" alone let somebody who changed the text approve it once another person had saved after
 * them). `b.editors`: every staff id that made or changed it (the server reads them from banner_editor, which retention does not prune).
 */
function ownedBy(b, actor) {
  if (!actor) return false;
  return actor.id === b.createdBy || Boolean(b.updatedBy && actor.id === b.updatedBy) || (Array.isArray(b.editors) && b.editors.includes(actor.id));
}

/**
 * Who may do what to this banner, now. `actor`: { id, can(permission) }. The panel shows exactly these buttons, and
 * the server refuses anything else with the same answer `transition()` gives.
 */
function allowed(b, actor, now = new Date()) {
  const own = ownedBy(b, actor);
  const manage = Boolean(actor && actor.can('screens.manage'));
  const out = [];
  if (b.status === 'draft' && manage) out.push('edit');
  // Owner, 2026-10-23: the writer publishes their own banner; an approver may publish anybody's.
  if (b.status === 'draft' && actor && (actor.can('banners.approve') || (own && manage))) out.push('approve');
  if (SHOWN_STATUSES.includes(b.status) && manage) out.push('cancel');
  if (['published', 'scheduled', 'cancelled'].includes(b.status) && manage) out.push('withdraw');
  if (b.status !== 'archived' && manage) out.push('archive');
  void now;
  return out;
}

const no = (http, code, message_no) => ({ ok: false, http, code, message_no });

/**
 * One step of the lifecycle. `to`: 'published' (approve: publish now, or schedule — POST /admin/banners/:id/approve)
 * · 'cancelled' · 'draft' (withdraw) · 'archived' (POST …/status). → { ok, patch, audit } or a refusal { ok: false, http, code, message_no } that changes nothing.
 * `patch` is column → value; `audit` is the action for the activity log.
 */
function transition(b, to, actor, now = new Date(), zone = null) {
  if (!STATUSES.includes(to) || to === 'scheduled') return no(400, 'invalid', 'status må være published, cancelled, draft eller archived.');
  // Publishing: the writer (screens.manage, own banner) or an approver (banners.approve, which needs no screens.manage);
  // every other step needs screens.manage.
  if (!actor || (to !== 'published' && !actor.can('screens.manage'))) return no(403, 'not_permitted', 'Ingen tilgang.');
  if (b.status === 'archived') {
    if (to === 'archived') return { ok: true, patch: {}, audit: null };
    return no(409, 'archived', 'Et nedtatt banner kan ikke publiseres igjen. Lag et nytt.');
  }
  if (to === 'archived') return { ok: true, patch: { status: 'archived' }, audit: 'banner.archive' };
  if (to === 'cancelled') {
    if (!SHOWN_STATUSES.includes(b.status)) return no(409, 'not_approved', 'Bare et godkjent banner kan stoppes.');
    return { ok: true, patch: { status: 'cancelled' }, audit: 'banner.cancel' };
  }
  if (to === 'draft') {
    if (b.status === 'draft') return { ok: true, patch: {}, audit: null };
    return { ok: true, patch: { status: 'draft', approved_by: null, approved_at: null }, audit: 'banner.withdraw' };
  }
  // to === 'published': the approval.
  const writer = ownedBy(b, actor) && actor.can('screens.manage');
  if (!actor.can('banners.approve') && !writer) {
    return no(403, 'not_permitted', 'Et banner publiseres av den som laget det, eller av en med rett til å godkjenne bannere.');
  }
  if (b.status !== 'draft') return no(409, 'not_draft', 'Bare et utkast kan publiseres. Ta det tilbake til utkast først.');
  if (!validDate(b.startsAt) || !validDate(b.endsAt)) return no(400, 'invalid_date', 'Datoen kan ikke leses.');
  const sch = schedule(b.startsAt || null, b.endsAt || null, now, zone);
  if (!sch.ok && sch.reason === 'invalid_date') return no(400, 'invalid_date', 'Datoen kan ikke leses.');
  if (!sch.ok) return no(400, 'ends_before_starts', 'Sluttidspunktet må være etter starttidspunktet.');
  if (ms(sch.endsAt) <= now.getTime()) return no(400, 'already_ended', 'Sluttidspunktet har allerede vært. Velg nye datoer.');
  if (b.inApp && sch.days > MAX_APP_DAYS) {
    return no(400, 'app_window_too_long', `Et banner i appen kan vises i høyst ${MAX_APP_DAYS} dager. Velg en tidligere slutt.`);
  }
  const later = ms(sch.startsAt) > now.getTime();
  return { ok: true, audit: later ? 'banner.schedule' : 'publish', autoExpiry: sch.autoExpiry,
    patch: { status: later ? 'scheduled' : 'published', starts_at: sch.startsAt, ends_at: sch.endsAt,
             approved_by: actor.id, approved_at: now.toISOString() } };
}

/**
 * An edit of a DRAFT: which fields, checked. `body`: { layers?, palette?, startsAt?, endsAt?, inApp?, name? } — only
 * what is present changes. → { ok, fields } (column → value, without the layers, which the caller fits with
 * validate()) or a refusal.
 */
function checkEdit(b, body, now = new Date()) {
  if (b.status !== 'draft') return no(409, 'not_draft', 'Bare et utkast kan endres. Ta banneret tilbake til utkast først — da må det godkjennes på nytt.');
  const f = {};
  if ('palette' in body) {
    if (!own(PALETTES, body.palette)) return no(400, 'invalid', 'Ukjente farger.');
    f.palette = body.palette;
  }
  for (const [k, col] of [['startsAt', 'starts_at'], ['endsAt', 'ends_at']]) {
    if (!(k in body)) continue;
    if (!validDate(body[k])) return no(400, 'invalid_date', 'Datoen kan ikke leses.');
    f[col] = body[k] || null;
  }
  const s = 'starts_at' in f ? f.starts_at : b.startsAt, e = 'ends_at' in f ? f.ends_at : b.endsAt;
  if (s && e && ms(e) <= ms(s)) return no(400, 'ends_before_starts', 'Sluttidspunktet må være etter starttidspunktet.');
  if ('inApp' in body) {
    if (typeof body.inApp !== 'boolean') return no(400, 'invalid', 'inApp må være true eller false.');
    f.in_app = body.inApp ? 1 : 0;
  }
  // The studio's ornament choice (2026-10-31): the template's own (null), none, or one of ORNAMENTS.
  if ('decor' in body) {
    if (!validDecor(body.decor)) return no(400, 'invalid', 'Ukjent pynt.');
    f.decor = body.decor || null;
  }
  void now;
  return { ok: true, fields: f };
}

module.exports = {
  TEMPLATES, TEMPLATE_ORDER, PALETTES, ARABIC, ORNAMENTS, translatable, SAFE_INSET_PERCENT, TITLE_SAFE_PERCENT,
  MIN_SIZE_PCT, DEFAULT_RUN_DAYS, STATUSES, SHOWN_STATUSES, MAX_APP_DAYS, MEMBER_LIMIT,
  template, palette, estimateLines, validate, fitSize, render, checkPalette, schedule, active, decorOf, validDecor,
  stateOf, isLive, allowed, transition, checkEdit, ownedBy, shownStatus,
  instantOf, inWindow, isLiveNow, publicCacheSeconds,
};
